import { Flex, Form, InputNumber, Slider, Space, Tag, Typography, notification } from 'antd';
import type { FormInstance } from 'antd';
import type {
  UpdateCheckJSONPublishResult,
  UpdateManifest,
  UpdateManifestModule,
  UpdateModuleStatus,
  UpdateModuleVersionRecord,
  UpdateReleaseStatus,
  UpdateRolloutStatus,
} from '../api/go';

export type RolloutPercentForm = {
  download_percent?: number;
  use_percent?: number;
  status: UpdateRolloutStatus;
};

type RolloutSubmitPercentValues = {
  download_percent?: number;
  use_percent?: number;
  status: UpdateRolloutStatus;
};

type RolloutSummary = {
  download_percent: number;
  use_percent: number;
  status: UpdateRolloutStatus;
};

type StatusSelectOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type RolloutStatusRecord = {
  id: number;
  status: UpdateRolloutStatus;
};

type RolloutModuleState = {
  rolloutCount: number;
  officialCount: number;
  grayCountExcludingCurrent: number;
};

export const ROLLOUT_STATUS_DRAFT: UpdateRolloutStatus = 'draft';
export const ROLLOUT_STATUS_GRAY: UpdateRolloutStatus = 'gray';
export const ROLLOUT_STATUS_OFFICIAL: UpdateRolloutStatus = 'official';
export const ROLLOUT_STATUS_ARCHIVED: UpdateRolloutStatus = 'archived';
const OFFICIAL_ROLLOUT_PERCENT = 100;

export const RELEASE_STATUS_DRAFT: UpdateReleaseStatus = 'draft';
export const RELEASE_STATUS_ENABLED: UpdateReleaseStatus = 'enabled';
export const RELEASE_STATUS_BASELINE: UpdateReleaseStatus = 'baseline';
export const RELEASE_STATUS_ARCHIVED: UpdateReleaseStatus = 'archived';

const MODULE_STATUS_DISABLED: UpdateModuleStatus = 'disabled';
export const MODULE_STATUS_ENABLED: UpdateModuleStatus = 'enabled';

export const releaseStatusOptions: StatusSelectOption<UpdateReleaseStatus>[] = [
  { value: RELEASE_STATUS_DRAFT, label: '草稿' },
  { value: RELEASE_STATUS_ENABLED, label: '可用' },
  { value: RELEASE_STATUS_BASELINE, label: '基线版本' },
  { value: RELEASE_STATUS_ARCHIVED, label: '归档' },
];

export const moduleStatusOptions = [
  { value: MODULE_STATUS_DISABLED, label: '禁用' },
  { value: MODULE_STATUS_ENABLED, label: '可用' },
];

export const rolloutStatusOptions: StatusSelectOption<UpdateRolloutStatus>[] = [
  { value: ROLLOUT_STATUS_DRAFT, label: '草稿' },
  { value: ROLLOUT_STATUS_GRAY, label: '灰度版本' },
  { value: ROLLOUT_STATUS_OFFICIAL, label: '正式版本' },
  { value: ROLLOUT_STATUS_ARCHIVED, label: '归档' },
];

export const releaseStatusMap: Record<UpdateReleaseStatus, { text: string; color: string }> = {
  draft: { text: '草稿', color: 'default' },
  enabled: { text: '可用', color: 'success' },
  baseline: { text: '基线版本', color: 'processing' },
  archived: { text: '归档', color: 'volcano' },
};

export const moduleStatusMap: Record<UpdateModuleStatus, { text: string; color: string }> = {
  disabled: { text: '禁用', color: 'default' },
  enabled: { text: '可用', color: 'success' },
};

const rolloutStatusMap: Record<UpdateRolloutStatus, { text: string; color: string }> = {
  draft: { text: '草稿', color: 'default' },
  gray: { text: '灰度版本', color: 'processing' },
  official: { text: '正式版本', color: 'success' },
  archived: { text: '归档', color: 'volcano' },
};

const UPDATE_DOWNLOAD_PREFIX = 'https://web.16dota.com.cn/';

export async function getErrorMessage(err: any) {
  if (err?.json) {
    try {
      const body = await err.json();
      return body?.error || body?.message || `请求失败: ${err.status}`;
    } catch {
      return `请求失败: ${err.status}`;
    }
  }
  if (err?.status) {
    return `请求失败: ${err.status}`;
  }
  if (err?.message) {
    return err.message;
  }
  return '请求失败';
}

export function showCheckJSONPublishWarning(result?: UpdateCheckJSONPublishResult) {
  if (!result?.error) {
    return;
  }
  notification.warning({
    message: 'check.json 发布失败',
    description: `${result.bucket}/${result.key}: ${result.error}`,
  });
}

export function renderStatusTag<T extends string>(status: T, dict: Partial<Record<T, { text: string; color: string }>>) {
  const item = dict[status];
  if (!item) {
    return <Tag>{status}</Tag>;
  }
  return <Tag color={item.color}>{item.text}</Tag>;
}

export function releaseStatusText(status: UpdateReleaseStatus) {
  return releaseStatusMap[status]?.text || String(status);
}

function allowedReleaseTargetStatuses(status: UpdateReleaseStatus) {
  if (status === RELEASE_STATUS_DRAFT) {
    return [RELEASE_STATUS_ENABLED, RELEASE_STATUS_BASELINE, RELEASE_STATUS_ARCHIVED];
  }
  if (status === RELEASE_STATUS_ENABLED) {
    return [RELEASE_STATUS_BASELINE, RELEASE_STATUS_ARCHIVED];
  }
  if (status === RELEASE_STATUS_BASELINE) {
    return [RELEASE_STATUS_ARCHIVED];
  }
  return [];
}

export function buildReleaseStatusSwitchOptions(currentStatus: UpdateReleaseStatus) {
  const targetStatuses = new Set(allowedReleaseTargetStatuses(currentStatus));
  return releaseStatusOptions.map((option) => ({
    ...option,
    label: option.value === currentStatus ? `${option.label}（当前）` : option.label,
    disabled: !targetStatuses.has(option.value),
  }));
}

export function canCreateRolloutFromRelease(status?: UpdateReleaseStatus) {
  return status === RELEASE_STATUS_ENABLED || status === RELEASE_STATUS_BASELINE;
}

export function canManageModuleRollout(row: UpdateModuleVersionRecord) {
  return row.status === MODULE_STATUS_ENABLED && canCreateRolloutFromRelease(row.release_status);
}

function canChangeRolloutStatus(from: UpdateRolloutStatus, to: UpdateRolloutStatus) {
  if (from === ROLLOUT_STATUS_ARCHIVED) {
    return false;
  }
  if (from === to) {
    return true;
  }
  if (from === ROLLOUT_STATUS_DRAFT) {
    return to === ROLLOUT_STATUS_GRAY || to === ROLLOUT_STATUS_OFFICIAL || to === ROLLOUT_STATUS_ARCHIVED;
  }
  if (from === ROLLOUT_STATUS_GRAY) {
    return to === ROLLOUT_STATUS_OFFICIAL || to === ROLLOUT_STATUS_ARCHIVED;
  }
  if (from === ROLLOUT_STATUS_OFFICIAL) {
    return to === ROLLOUT_STATUS_ARCHIVED;
  }
  return false;
}

export function buildRolloutModuleState(rollouts: RolloutStatusRecord[], currentID?: number): RolloutModuleState {
  return rollouts.reduce<RolloutModuleState>(
    (state, rollout) => {
      state.rolloutCount += 1;
      if (rollout.status === ROLLOUT_STATUS_OFFICIAL) {
        state.officialCount += 1;
      }
      if (rollout.status === ROLLOUT_STATUS_GRAY && rollout.id !== currentID) {
        state.grayCountExcludingCurrent += 1;
      }
      return state;
    },
    {
      rolloutCount: 0,
      officialCount: 0,
      grayCountExcludingCurrent: 0,
    },
  );
}

function canSetRolloutStatus(targetStatus: UpdateRolloutStatus, current: RolloutStatusRecord | undefined, state: RolloutModuleState) {
  if (current && !canChangeRolloutStatus(current.status, targetStatus)) {
    return false;
  }
  if (!current && state.rolloutCount === 0 && targetStatus !== ROLLOUT_STATUS_OFFICIAL) {
    return false;
  }
  if (targetStatus === ROLLOUT_STATUS_GRAY && state.grayCountExcludingCurrent > 0) {
    return false;
  }

  let officialCount = state.officialCount;
  if (!current) {
    if (targetStatus === ROLLOUT_STATUS_OFFICIAL) {
      officialCount += 1;
    }
  } else {
    if (current.status !== ROLLOUT_STATUS_OFFICIAL && targetStatus === ROLLOUT_STATUS_OFFICIAL) {
      officialCount += 1;
    }
    if (current.status === ROLLOUT_STATUS_OFFICIAL && targetStatus !== ROLLOUT_STATUS_OFFICIAL) {
      officialCount -= 1;
    }
  }
  return officialCount > 0;
}

export function buildRolloutStatusSelectOptions(current: RolloutStatusRecord | undefined, state: RolloutModuleState, ready: boolean) {
  return rolloutStatusOptions.map((option) => ({
    ...option,
    label: option.value === current?.status ? `${option.label}（当前）` : option.label,
    disabled: !ready || !canSetRolloutStatus(option.value, current, state),
  }));
}

export function isDisabledStatusOption<T extends string>(options: StatusSelectOption<T>[], value?: T) {
  if (!value) {
    return true;
  }
  return options.find((option) => option.value === value)?.disabled !== false;
}

export function isOfficialRolloutStatus(status?: UpdateRolloutStatus) {
  return status === ROLLOUT_STATUS_OFFICIAL;
}

export function isArchivedRolloutStatus(status?: UpdateRolloutStatus) {
  return status === ROLLOUT_STATUS_ARCHIVED;
}

export function canEditRolloutPercent(status?: UpdateRolloutStatus) {
  return status === ROLLOUT_STATUS_DRAFT || status === ROLLOUT_STATUS_GRAY;
}

export function officialRolloutPercentValues() {
  return {
    download_percent: OFFICIAL_ROLLOUT_PERCENT,
    use_percent: OFFICIAL_ROLLOUT_PERCENT,
  };
}

export function normalizeRolloutSubmitValues<T extends RolloutPercentForm>(
  values: T,
): Omit<T, 'download_percent' | 'use_percent'> & RolloutSubmitPercentValues {
  if (isOfficialRolloutStatus(values.status)) {
    return { ...values, ...officialRolloutPercentValues() };
  }
  if (isArchivedRolloutStatus(values.status)) {
    const { download_percent, use_percent, ...rest } = values;
    return rest;
  }
  return values;
}

export function handleRolloutPercentValuesChange(
  form: FormInstance<any>,
  changed: Partial<RolloutPercentForm>,
  values: RolloutPercentForm,
) {
  if ('status' in changed && isOfficialRolloutStatus(values.status)) {
    form.setFieldsValue(officialRolloutPercentValues());
    return;
  }
  if (!canEditRolloutPercent(values.status) || !('download_percent' in changed)) {
    return;
  }

  const downloadPercent = values.download_percent ?? 0;
  const usePercent = values.use_percent ?? 0;
  if (usePercent > downloadPercent) {
    form.setFieldValue('use_percent', downloadPercent);
  }
}

export function renderRolloutSummary(rollout?: RolloutSummary) {
  if (!rollout) {
    return <Typography.Text type="secondary">未配置</Typography.Text>;
  }
  if (!canEditRolloutPercent(rollout.status)) {
    return renderStatusTag(rollout.status, rolloutStatusMap);
  }
  return (
    <Space direction="vertical" size={0}>
      {renderStatusTag(rollout.status, rolloutStatusMap)}
      <Typography.Text type="secondary">
        下载 {rollout.download_percent}% / 使用 {rollout.use_percent}%
      </Typography.Text>
    </Space>
  );
}

export function nullableText(value?: string | number | null) {
  if (value === undefined || value === null || value === '') {
    return <Typography.Text type="secondary">-</Typography.Text>;
  }
  return value;
}

export function renderMultilineText(value?: string | null) {
  if (!value) {
    return <Typography.Text type="secondary">-</Typography.Text>;
  }
  return <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{value}</Typography.Paragraph>;
}

function buildUpdateDownloadURL(ossKey?: string) {
  const rawKey = String(ossKey || '').trim();
  if (/^https?:\/\//i.test(rawKey)) {
    return rawKey;
  }
  const key = rawKey.replace(/^\/+/, '');
  return key ? `${UPDATE_DOWNLOAD_PREFIX}${key}` : '';
}

export function renderOssKey(value: string, downloadURL?: string) {
  const fullURL = downloadURL || buildUpdateDownloadURL(value);
  return (
    <Space direction="vertical" size={0}>
      <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>
      {fullURL ? (
        <Typography.Link href={fullURL} target="_blank" copyable={{ text: fullURL }}>
          {fullURL}
        </Typography.Link>
      ) : null}
    </Space>
  );
}

export function parseJSONErrorMessage(err: unknown, raw: string) {
  const message = err instanceof Error ? err.message : String(err);
  if (/line \d+ column \d+/i.test(message)) {
    return message;
  }

  const positionMatch = message.match(/position\s+(\d+)/i);
  if (!positionMatch) {
    return message;
  }

  const position = Number(positionMatch[1]);
  if (!Number.isFinite(position) || position < 0) {
    return message;
  }

  const before = raw.slice(0, position);
  const line = before.split(/\r\n|\r|\n/).length;
  const lastLineBreak = Math.max(before.lastIndexOf('\n'), before.lastIndexOf('\r'));
  const column = position - lastLineBreak;
  return `${message}（第 ${line} 行，第 ${column} 列）`;
}

export function normalizeManifest(raw: unknown): UpdateManifest {
  const value = raw as Partial<UpdateManifest>;
  if (
    typeof value?.quick_version !== 'number' ||
    typeof value?.build_number !== 'number' ||
    typeof value?.install_sha1 !== 'string' ||
    typeof value?.install_url !== 'string' ||
    !Array.isArray(value?.modules)
  ) {
    throw new Error('manifest 必须包含 quick_version、build_number、install_sha1、install_url 和 modules');
  }

  const installURL = value.install_url.trim();
  const installSHA1 = value.install_sha1.trim();
  if (!installURL || !installSHA1) {
    throw new Error('manifest 必须包含非空 install_url 和 install_sha1');
  }

  return {
    quick_version: value.quick_version,
    build_number: value.build_number,
    install_sha1: installSHA1,
    install_url: installURL,
    modules: value.modules.map((item) => {
      const module = item as Partial<UpdateManifestModule>;
      return {
        key: String(module.key || '').trim(),
        version: String(module.version || '').trim(),
        url: String(module.url || '').trim(),
        sha1: String(module.sha1 || '').trim(),
      };
    }),
  };
}

export function groupModules(records: UpdateModuleVersionRecord[]) {
  const groups = new Map<string, UpdateModuleVersionRecord[]>();
  for (const record of records) {
    const list = groups.get(record.module_key) || [];
    list.push(record);
    groups.set(record.module_key, list);
  }
  return Array.from(groups.entries()).map(([moduleKey, rows]) => ({ moduleKey, rows }));
}

export function PercentFields(props: { downloadMax?: number; disabled?: boolean }) {
  const { downloadMax = 100, disabled } = props;

  return (
    <>
      <Form.Item<RolloutPercentForm> label="下载灰度" required>
        <Flex gap="middle" align="center">
          <Form.Item name="download_percent" noStyle rules={[{ required: true, message: '请填写下载灰度' }]}>
            <Slider min={0} max={100} style={{ flex: 1 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="download_percent" noStyle>
            <InputNumber min={0} max={100} style={{ width: 96 }} disabled={disabled} />
          </Form.Item>
        </Flex>
      </Form.Item>
      <Form.Item<RolloutPercentForm> label="使用灰度" required>
        <Flex gap="middle" align="center">
          <Form.Item name="use_percent" noStyle rules={[{ required: true, message: '请填写使用灰度' }]}>
            <Slider min={0} max={downloadMax} style={{ flex: 1 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="use_percent" noStyle>
            <InputNumber min={0} max={downloadMax} style={{ width: 96 }} disabled={disabled} />
          </Form.Item>
        </Flex>
      </Form.Item>
    </>
  );
}
